import React, { Fragment, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Layout as LayoutWrapper } from 'antd';
import { ROUTES } from 'constant';
import { postTokenVerify } from 'modules/Auth/Auth.actions';
import { Header } from './Header/Header';
import Footer from './Footer/Footer';
import { Player } from './Player';
import { EventsState } from 'modules/Events/Event.interfaces';
import LanguageSelector from 'components/LanguageSelector';
import { GlobalState, ILanguage } from 'modules/Global/Global.interfaces';
import { setHomePageState } from 'modules/Global/Global.actions';
import { setUserLanguage } from 'utils/helpers';
import Branding from './Branding';
import { handleOpenPlayer } from '../../modules/Events/Events.actions';
import { useWindowSize } from 'utils/hooks';

interface ILayout {
  children?: ReactNode;
  location?: Location;
}

const { Content } = LayoutWrapper;

const checkAvailableLocation = (location: any) => {
  if (location.location) {
    return Object.values(ROUTES.AUTH).some((i) => location.location.pathname.includes(i));
  }
  return Object.values(ROUTES.AUTH).some((i) => location.pathname.includes(i));
};

const RenderLogUserTemplate = ({ children }: { children?: ReactNode }) => {
  const {
    playerList: { list, account },
  } = useSelector(({ eventsReducer }: { eventsReducer: EventsState }) => eventsReducer);
  const location = useLocation();
  const { width } = useWindowSize();
  const isMobile = width && width < 768;
  const isEventsPage = location.pathname === '/' || location.pathname.startsWith('/events');

  // Always show player on mobile events page, otherwise show only if has content
  const PlayerContainer = useCallback(() => {
    if (isMobile && isEventsPage) {
      return <Player />;
    }
    return Boolean(list.length) && Boolean(account.length) && <Player />;
  }, [list, account, isMobile, isEventsPage]);

  const [withBackground, setWithBackground] = useState(
    location.pathname.startsWith(ROUTES.PROFILE.AGENT_ACCOUNT_SETTINGS_PAGE),
  );

  useEffect(() => {
    setWithBackground(location.pathname.startsWith(ROUTES.PROFILE.AGENT_ACCOUNT_SETTINGS_PAGE));
  }, [location]);

  // Hide footer on all mobile pages
  const shouldHideFooter = width && width < 768;

  return (
    <div className={withBackground ? 'with-background' : ''}>
      <Header />
      <Content>{children}</Content>
      {!shouldHideFooter && <Footer />}
      {PlayerContainer()}
    </div>
  );
};

export const IframeLayout = ({ children }: ILayout) => {
  const dispatch = useDispatch();
  const { homePageState } = useSelector(({ globalReducer }: { globalReducer: GlobalState }) => globalReducer);
  const {
    playerList: { list, account },
  } = useSelector(({ eventsReducer }: { eventsReducer: EventsState }) => eventsReducer);
  const PlayerContainer = useCallback(() => Boolean(list.length) && Boolean(account.length) && <Player />, [
    list,
    account,
  ]);

  const handleLanguageSelect = (selectedLanguage: ILanguage) => {
    setUserLanguage(selectedLanguage);
    dispatch(handleOpenPlayer.request({ list: [], account: null, index: 0 }));
    dispatch(
      setHomePageState({
        ...homePageState,
        selectedLanguage: selectedLanguage,
      }),
    );
  };

  return (
    <>
      <LanguageSelector handleLanguageSelect={handleLanguageSelect} />
      <Content>{children}</Content>
      {PlayerContainer()}
      <Branding />
    </>
  );
};
export const AuthLayout = ({ children }: ILayout) => {
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(postTokenVerify.request({ data: { token } }));
    }
  }, []);

  return (
    <>
      {checkAvailableLocation(location) ? (
        <Content>{children}</Content>
      ) : (
        <RenderLogUserTemplate>{children}</RenderLogUserTemplate>
      )}
    </>
  );
};
